import { useState } from 'react'
import { GripVertical, Trash2, Plus, Library, ChevronDown, ChevronRight, Bookmark } from 'lucide-react'
import { useSortable, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useDroppable } from '@dnd-kit/core'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { FieldBlock } from './FieldBlock'
import { FieldTypePicker } from './FieldTypePicker'
import { LibraryFieldPicker } from './LibraryFieldPicker'
import { SaveSectionToLibraryDialog } from './SaveSectionToLibraryDialog'
import type { ApplicationSection, ApplicationField, ApplicationFieldType } from '@/types'

interface Props {
  section: ApplicationSection
  onUpdate: (patch: Partial<Pick<ApplicationSection, 'title' | 'description'>>) => void
  onDelete: () => void
  onAddField: (type: ApplicationFieldType) => void
  onAddFieldFromLibrary: (libraryId: string) => Promise<void>
  onEditField: (field: ApplicationField) => void
  onDeleteField: (fieldId: string) => void
}

function SectionDropZone({ sectionId, empty }: { sectionId: string; empty: boolean }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `section-dropzone:${sectionId}`,
    data: { type: 'section-dropzone', sectionId },
  })
  return (
    <div
      ref={setNodeRef}
      className={`rounded-md border-2 border-dashed transition-colors ${
        isOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/20'
      } ${empty ? 'py-6' : 'py-2'}`}
    >
      <p className="text-center text-xs text-muted-foreground">
        {empty ? 'Drop a field here, or add one below.' : isOver ? 'Drop to add to this section' : ''}
      </p>
    </div>
  )
}

export function SectionBlock({
  section,
  onUpdate,
  onDelete,
  onAddField,
  onAddFieldFromLibrary,
  onEditField,
  onDeleteField,
}: Props) {
  const [collapsed, setCollapsed] = useState(false)
  const [addFieldOpen, setAddFieldOpen] = useState(false)
  const [libraryOpen, setLibraryOpen] = useState(false)
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false)

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const sortedFields = [...section.fields].sort((a, b) => a.order - b.order)

  return (
    <>
      <div ref={setNodeRef} style={style} className="rounded-lg border bg-card shadow-sm">
        {/* Section header */}
        <div className="flex items-start gap-2 p-3">
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="mt-1 cursor-grab touch-none text-muted-foreground hover:text-foreground active:cursor-grabbing"
            aria-label="Drag to reorder section"
          >
            <GripVertical className="h-4 w-4" />
          </button>

          <button
            type="button"
            className="mt-1 text-muted-foreground hover:text-foreground"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? 'Expand section' : 'Collapse section'}
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>

          <div className="min-w-0 flex-1 space-y-2">
            <Input
              value={section.title}
              onChange={(e) => onUpdate({ title: e.target.value })}
              placeholder="Section title…"
              className="text-sm font-medium"
            />
            {!collapsed && (
              <Textarea
                value={section.description ?? ''}
                onChange={(e) => onUpdate({ description: e.target.value })}
                placeholder="Section description (optional)…"
                rows={2}
                className="text-sm"
              />
            )}
          </div>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="mt-1 shrink-0 text-destructive hover:text-destructive"
            onClick={onDelete}
            aria-label="Delete section"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>

        {/* Fields */}
        {!collapsed && (
          <div className="px-3 pb-3">
            <SortableContext
              items={sortedFields.map((f) => f.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {sortedFields.map((field) => (
                  <FieldBlock
                    key={field.id}
                    field={field}
                    onEdit={() => onEditField(field)}
                    onDelete={() => onDeleteField(field.id)}
                  />
                ))}
              </div>
            </SortableContext>

            <div className="mt-2">
              <SectionDropZone sectionId={section.id} empty={sortedFields.length === 0} />
            </div>

            <div className="mt-3 flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setAddFieldOpen(true)}
              >
                <Plus className="mr-1 h-3.5 w-3.5" />
                Add Field
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setLibraryOpen(true)}
              >
                <Library className="mr-1 h-3.5 w-3.5" />
                From Library
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="ml-auto"
                onClick={() => setSaveTemplateOpen(true)}
                disabled={section.fields.length === 0}
                title={section.fields.length === 0 ? 'Add fields before saving as template' : ''}
              >
                <Bookmark className="mr-1 h-3.5 w-3.5" />
                Save as Template
              </Button>
            </div>
          </div>
        )}
      </div>

      <FieldTypePicker
        open={addFieldOpen}
        onOpenChange={setAddFieldOpen}
        onSelect={onAddField}
      />

      <LibraryFieldPicker
        open={libraryOpen}
        onOpenChange={setLibraryOpen}
        onSelect={onAddFieldFromLibrary}
      />

      <SaveSectionToLibraryDialog
        open={saveTemplateOpen}
        onOpenChange={setSaveTemplateOpen}
        section={section}
        onSaved={() => { /* no-op; the form builder state is unchanged */ }}
      />
    </>
  )
}
